import React, { useState, useEffect, useRef } from "react";
import { bindActionCreators } from "redux";
import { connect } from "react-redux";
import SimpleReactValidator from "simple-react-validator";
import SearchSelect from "components/common/SearchSelect";
import { closeCustomModal, openCustomModal } from "components/custom/modals";
import Spinner from "components/common/Spinner";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import {
    profileGetContactInfo,
    profileSetContactInfo,
    profileSetPassword,
} from "api/menu/profile/actions";
import {
    fetchCities
} from "api/menu/master-files/locations/actions";

const eye = <FontAwesomeIcon icon={faEye}/>;
const eyeSlash = <FontAwesomeIcon icon={faEyeSlash}/>;
const EditPasswordModal =
    React.lazy(() => import("components/menu/master-files/workers-maintenance/worker-profile-components/edit-password-modal"));

const TabContactInformation = (props) => {
    const {
        setUnsaved,
        contactInfo,
        profileGetContactInfo,
        profileSetContactInfo,
        loading,

        fetchCities,
        provinces,
        cities,
    } = props;
    const [passwordShown, setPasswordShown] = useState(false);
    const [bankShown, setBankShown] = useState(false);
    const [branchShown, setBranchShown] = useState(false);
    const [accountShown, setAccountShown] = useState(false);

    const togglePasswordVisibility = () => {
        setPasswordShown(!passwordShown);
    };

    const toggleBankVisibility = () => {
        setBankShown(!bankShown);
    };

    const toggleBranchVisibility = () => {
        setBranchShown(!branchShown);
    };

    const toggleAccountVisibility = () => {
        setAccountShown(!accountShown);
    };

    const simpleValidator = useRef(new SimpleReactValidator({
        locale: "en",
        element: message => <div className="text-danger">{message}</div>
    }));

    const [userProfile, setUserProfile] = useState(contactInfo);

    const handleSave = (dataForSave) => {
            if (simpleValidator.current.allValid()) {
                profileSetContactInfo(dataForSave);
                setUnsaved(false);
            }
        },
        onChange = (arr) => {
            const newUserProfile = Object.assign({}, userProfile);
            arr.forEach(item => {
                newUserProfile[item.key] = item.value;
            });
            setUserProfile(newUserProfile);
            setUnsaved(() => handleSave(newUserProfile));
        };

    const handleResetPassword = () => {
        const modalObj = {
            id: "reset-password",
            header: "Change Password",
            modalSettings: {
                size: "sm"
            },
            children: (
                <React.Suspense
                    fallback={<div className="m-2 text-center font-weight-bold">Loading...</div>}>
                    <EditPasswordModal
                        onCancel={() => props.closeCustomModal(modalObj)}
                        onSubmit={password => {
                            props.profileSetPassword(password);
                            props.closeCustomModal(modalObj);
                        }}
                    />
                </React.Suspense>
            ),
            handleClose: () => props.closeCustomModal(modalObj)
        };
        props.openCustomModal(modalObj);
    };

    useEffect(() => {
        profileGetContactInfo();
    }, [profileGetContactInfo]);

    useEffect(() => {
        setUserProfile(contactInfo);
        if (contactInfo.provinceId) {
            fetchCities(contactInfo.provinceId);
        }
    }, [contactInfo]);

    const provincesOptions = provinces.map(value => ({
            value: value.id,
            label: value.fullName
        })),
        citiesOptions = cities.map(value => ({
            value: value.id,
            label: value.name
        }));

    return (
        <div className="position-relative py-4">
            {!loading || <Spinner/>}

            <h3>Profile</h3>
            <div className="row mb-4">
                <div className="col-6">
                    <div className="form-group">
                        <label>First Name</label>
                        <input
                            className="form-control"
                            type="text"
                            value={userProfile.firstName || ""}
                            onChange={ev => onChange([{
                                key: "firstName",
                                value: ev.target.value
                            }])}
                            onBlur={simpleValidator.current.showMessages("First Name")}
                        />
                        {simpleValidator.current.message("First Name", userProfile.firstName, "required")}
                    </div>
                    <div className="form-group">
                        <label>Last Name</label>
                        <input
                            className="form-control"
                            type="text"
                            value={userProfile.lastName || ""}
                            onChange={ev => onChange([{
                                key: "lastName",
                                value: ev.target.value
                            }])}
                            onBlur={simpleValidator.current.showMessages("Last Name")}
                        />
                        {simpleValidator.current.message("Last Name", userProfile.lastName, "required")}
                    </div>
                    <div className="form-group">
                        <label>Email</label>
                        <input
                            className="form-control"
                            type="email"
                            value={userProfile.email || ""}
                            onChange={ev => onChange([{
                                key: "email",
                                value: ev.target.value
                            }])}
                            onBlur={simpleValidator.current.showMessages("Email")}
                        />
                        {simpleValidator.current.message("Email", userProfile.email, "required|email")}
                    </div>
                    <div className="form-group">
                        <label>Home Phone</label>
                        <input
                            className="form-control"
                            type="text"
                            value={userProfile.homePhone || ""}
                            onChange={ev => onChange([{
                                key: "homePhone",
                                value: ev.target.value
                            }])}
                        />
                    </div>
                    <div className="form-group">
                        <label>Cell Phone</label>
                        <input
                            className="form-control"
                            type="text"
                            value={userProfile.mobilePhone || ""}
                            onChange={ev => onChange([{
                                key: "mobilePhone",
                                value: ev.target.value
                            }])}
                        />
                    </div>
                </div>
                <div className="col-6">
                    <div className="form-group">
                        <label>Province</label>
                        <SearchSelect
                            className="custom-react-select"
                            isLoading={false}
                            isClearable={false}
                            isSearchable={true}
                            options={provincesOptions}
                            value={provincesOptions.find(item => item.value === (userProfile.provinceId || ""))}
                            onChange={option => {
                                onChange([{
                                    key: "province",
                                    value: option.label
                                }, {
                                    key: "provinceId",
                                    value: option.value
                                }, {
                                    key: "city",
                                    value: ""
                                }, {
                                    key: "cityId",
                                    value: null
                                }]);
                                fetchCities(option.value);
                            }}
                            onBlur={simpleValidator.current.showMessages("Province")}
                        />
                        {simpleValidator.current.message("Province", userProfile.province, "required")}
                    </div>
                    <div className="form-group">
                        <label>City</label>
                        <SearchSelect
                            className="custom-react-select"
                            isLoading={false}
                            isClearable={false}
                            isSearchable={true}
                            options={citiesOptions}
                            value={citiesOptions.find(item => item.value === (userProfile.cityId || ""))}
                            onChange={option => {
                                onChange([{
                                    key: "city",
                                    value: option.label
                                }, {
                                    key: "cityId",
                                    value: option.value
                                }]);
                            }}
                            onBlur={simpleValidator.current.showMessages("City")}
                        />
                        {simpleValidator.current.message("City", userProfile.city, "required")}
                    </div>
                    <div className="form-group">
                        <label>Address 1</label>
                        <input
                            className="form-control"
                            type="text"
                            value={userProfile.address1 || ""}
                            onChange={ev => onChange([{
                                key: "address1",
                                value: ev.target.value
                            }])}
                            onBlur={simpleValidator.current.showMessages("Address 1")}
                        />
                        {simpleValidator.current.message("Address 1", userProfile.address1, "required")}
                    </div>
                    <div className="form-group">
                        <label>Address 2</label>
                        <input
                            className="form-control"
                            type="text"
                            value={userProfile.address2 || ""}
                            onChange={ev => onChange([{
                                key: "address2",
                                value: ev.target.value
                            }])}
                        />
                    </div>
                    <div className="form-group">
                        <label>Postal Code</label>
                        <input
                            className="form-control"
                            type="text"
                            value={userProfile.postalCode || ""}
                            onChange={ev => onChange([{
                                key: "postalCode",
                                value: ev.target.value
                            }])}
                            onBlur={simpleValidator.current.showMessages("Postal Code")}
                        />
                        {simpleValidator.current.message("Postal Code", userProfile.postalCode, "required")}
                    </div>
                    <div className="form-group">
                        <label>SIN</label>
                        <div className="form-group">
                            <input
                                className="form-control"
                                type={passwordShown ? "text" : "password"}
                                value={userProfile.sin || ""}
                                autoComplete="off"
                                onChange={ev => onChange([{
                                    key: "sin",
                                    value: ev.target.value
                                }])}
                            />
                            <i className="sin"
                               onClick={togglePasswordVisibility}>{passwordShown ? eye : eyeSlash}</i>
                        </div>
                    </div>
                </div>
            </div>
            <h3>Banking details</h3>
            <div className="row">
                <div className="col-4">
                    <div className="form-group">
                        <label>Bank</label>
                        <div className="form-group">
                            <input
                                className="form-control"
                                type={bankShown ? "text" : "password"}
                                value={userProfile.bank || ""}
                                onChange={ev => onChange([{
                                    key: "bank",
                                    value: ev.target.value
                                }])}
                            />
                            {simpleValidator.current.message("Bank", userProfile.bank, "numeric")}
                            <i className="bank"
                               onClick={toggleBankVisibility}>{bankShown ? eye : eyeSlash}</i>
                        </div>
                    </div>
                </div>
                <div className="col-4">
                    <div className="form-group">
                        <label>Branch</label>
                        <input
                            className="form-control"
                            type={branchShown ? "text" : "password"}
                            value={userProfile.transit || ""}
                            onChange={ev => onChange([{
                                key: "transit",
                                value: ev.target.value
                            }])}
                        />
                        {simpleValidator.current.message("Branch", userProfile.transit, "numeric")}
                        <i className="bank"
                           onClick={toggleBranchVisibility}>{branchShown ? eye : eyeSlash}</i>
                    </div>
                </div>
                <div className="col-4">
                    <div className="form-group">
                        <label>Account</label>
                        <input
                            className="form-control"
                            type={accountShown ? "text" : "password"}
                            value={userProfile.account || ""}
                            onChange={ev => onChange([{
                                key: "account",
                                value: ev.target.value
                            }])}
                        />
                        {simpleValidator.current.message("Account", userProfile.account, "numeric")}
                        <i className="bank"
                           onClick={toggleAccountVisibility}>{accountShown ? eye : eyeSlash}</i>
                    </div>
                </div>
            </div>

            <div className="d-flex justify-content-between">
                <button
                    className="btn btn-warning"
                    onClick={handleResetPassword}
                >
                    Change Password
                </button>
                <button
                    className="btn btn-success"
                    onClick={() => handleSave(userProfile)}
                >
                    Save
                </button>
            </div>
        </div>
    );
};

const mapStateToProps = state => ({
    loading: state.profile.loading,
    contactInfo: state.profile.contactInfo,

    provinces: state.locations.provinces,
    cities: state.locations.cities,
});

const mapStateToDispatch = dispatch => ({
    openCustomModal: bindActionCreators(openCustomModal, dispatch),
    closeCustomModal: bindActionCreators(closeCustomModal, dispatch),

    profileGetContactInfo: bindActionCreators(profileGetContactInfo, dispatch),
    profileSetContactInfo: bindActionCreators(profileSetContactInfo, dispatch),
    profileSetPassword: bindActionCreators(profileSetPassword, dispatch),

    fetchCities: bindActionCreators(fetchCities, dispatch)
});


export default connect(
    mapStateToProps,
    mapStateToDispatch
)(TabContactInformation);
